import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useResolve } from '@/infrastructure/useResolve';
import { TOKEN_UsersStore } from './UsersStore';

export const UsersView = observer(function UsersView() {
  const store = useResolve(TOKEN_UsersStore);
  const [role, setRole] = useState('');

  useEffect(() => {
    void store.load();
  }, [store]);

  return (
    <div className="grid gap-6 md:grid-cols-[18rem_1fr]" data-testid="users-screen">
      <section className="rounded-lg border border-slate-200 dark:border-slate-800">
        <header className="flex items-center justify-between border-b border-slate-200 px-3 py-2 text-sm font-medium dark:border-slate-800">
          <span>Users</span>
          <button type="button" onClick={() => void store.load()} className="text-xs text-indigo-600 hover:underline">
            refresh
          </button>
        </header>
        {store.loading && <p className="px-3 py-2 text-sm text-slate-500">loading…</p>}
        <ul className="max-h-[28rem] overflow-auto" data-testid="user-list">
          {store.users.map((u) => (
            <li key={u.sub}>
              <button
                type="button"
                onClick={() => store.select(u.username)}
                data-testid={`user-${u.username}`}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                  store.selectedUsername === u.username ? 'bg-indigo-50 dark:bg-slate-800' : ''
                }`}
              >
                <span className={u.disabled ? 'line-through text-slate-400' : ''}>{u.username}</span>
                {u.disabled && <span className="rounded bg-rose-100 px-1.5 text-xs text-rose-700">disabled</span>}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-slate-200 p-4 dark:border-slate-800" data-testid="user-detail">
        {!store.selected && <p className="text-sm text-slate-500">Select a user.</p>}
        {store.selected && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">{store.selected.username}</h3>
              <p className="text-sm text-slate-500">{store.selected.email ?? '-'} · {store.selected.sub}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-slate-400">Roles</p>
              <div className="flex flex-wrap gap-2" data-testid="user-roles">
                {store.selected.roles.map((r) => (
                  <span key={r} className="flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-sm dark:bg-slate-800">
                    {r}
                    {r !== 'user' && (
                      <button type="button" title={`revoke ${r}`} onClick={() => void store.revokeRole(r)} className="text-slate-400 hover:text-rose-600">
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              <form
                className="mt-2 flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void store.assignRole(role).then(() => setRole(''));
                }}
              >
                <input
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="role name"
                  data-testid="role-input"
                  className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
                />
                <button type="submit" disabled={store.busy} data-testid="assign-role" className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:opacity-50">
                  assign
                </button>
              </form>
            </div>
            <div className="flex flex-wrap gap-2">
              {store.selected.disabled ? (
                <button type="button" onClick={() => void store.setEnabled(true)} disabled={store.busy} data-testid="enable-user" className="rounded border border-emerald-300 px-3 py-1 text-sm text-emerald-700">
                  enable
                </button>
              ) : (
                <button type="button" onClick={() => void store.setEnabled(false)} disabled={store.busy} data-testid="disable-user" className="rounded border border-rose-300 px-3 py-1 text-sm text-rose-700">
                  disable
                </button>
              )}
              <button type="button" onClick={() => void store.loadSessions()} disabled={store.busy} className="rounded border border-slate-300 px-3 py-1 text-sm dark:border-slate-700">
                sessions
              </button>
              <button type="button" onClick={() => void store.forceLogout()} disabled={store.busy} data-testid="force-logout" className="rounded border border-slate-300 px-3 py-1 text-sm dark:border-slate-700">
                log out everywhere
              </button>
            </div>
            <ul className="text-sm text-slate-600 dark:text-slate-300" data-testid="user-sessions">
              {store.sessions.map((s) => (
                <li key={s.session_id} className="font-mono text-xs">{s.session_id}</li>
              ))}
            </ul>
            {store.notice && <p className="text-sm text-emerald-600" data-testid="user-notice">{store.notice}</p>}
            {store.error && <p className="text-sm text-rose-600" data-testid="user-error">{store.error}</p>}
          </div>
        )}
      </section>
    </div>
  );
});
